import React, { useEffect, useState } from "react";
import {
  CalendarCheck,
  Megaphone,
  CalendarDays,
} from "lucide-react";
import { useUser } from "../../context/UserContext";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import api from "../../utils/api";
import AnimatedCounter from "../../components/AnimatedCounter";
import { TbClockPlus } from "react-icons/tb";
import { MdOutlineSick } from "react-icons/md";
import { FaIndianRupeeSign } from "react-icons/fa6";

function E_Dashboard() {
  const [leaveBalance, setLeaveBalance] = useState({
    totalLeavesTaken: 0,
    remainingEarnedLeaves: 0,
    remainingSickLeaves: 0,
    compOffs: 0,
    compPays: 0,
  });

  const [loading, setLoading] = useState(true);
  const { currentUser } = useUser();
  const empid = currentUser?.id;
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [upcomingHolidays, setUpcomingHolidays] = useState([]);

  useEffect(() => {
    api
      .get(`/api/holidays/upcoming`)
      .then((res) => setUpcomingHolidays(res.data))
      .catch((err) => console.error("Failed to fetch holidays", err));
  }, []);

  useEffect(() => {
    api
      .get(`/api/announcements/latest`)
      .then((res) => setAnnouncements(res.data))
      .catch((err) => console.error("Failed to fetch announcements", err));
  }, []);

  useEffect(() => {
    const fetchLeaveBalance = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/leaveBalance/${empid}`);
        console.log("Leave Balance Response:", response.data);
        setLeaveBalance({
          totalLeavesTaken: response.data?.totalLeavesTaken ?? 0,
          remainingEarnedLeaves: response.data?.earnedLeaves ?? 0,
          remainingSickLeaves: response.data?.sickLeaves ?? 0,
          compOffs: response.data?.compOffs ?? 0,
          compPays: response.data?.compPays ?? 0,
          totalEarnedLeaves : response.data?.totalEarnedLeaves ?? 0,
          totalSickLeaves : response.data?.totalSickLeaves ?? 0,
        });
      } catch (error) {
        setLeaveBalance({
          totalLeavesTaken: 0,
          remainingEarnedLeaves: 0,
          remainingSickLeaves: 0,
          compOffs: 0,
          compPays: 0,
          totalEarnedLeaves: 0,
          totalSickLeaves: 0,
        });
      } finally {
        setLoading(false);
      }
    };
    if (empid) fetchLeaveBalance();
  }, [empid]);

  const cards = [
 
    {
      title: "EL Balance",
      count: leaveBalance.remainingEarnedLeaves ?? "-",
      total: leaveBalance.totalEarnedLeaves ?? "-", // dummy total including carry forward 
      icon: <CalendarCheck size={24} />,
      gradient: "from-green-50 to-green-100/50",
      iconBg: "bg-green-100",
      iconColor: "text-green-600",
      textColor: "text-green-700",
    },
    {
      title: "SL Balance",
      count: leaveBalance.remainingSickLeaves ?? "-",
      total: leaveBalance.totalSickLeaves?? "-", // dummy total including carry forward 
      icon: <MdOutlineSick size={24} />,
      gradient: "from-purple-50 to-purple-100/50",
      iconBg: "bg-purple-100",
      iconColor: "text-purple-600",
      textColor: "text-purple-700",
    },
    {
      title: "Comp Off",
      count: leaveBalance.compOffs ?? "-",
      
      icon: <TbClockPlus size={24} />,
      gradient: "from-orange-50 to-orange-100/50",
      iconBg: "bg-orange-100",
      iconColor: "text-orange-600",
      textColor: "text-orange-700",
    },

       {
      title: "Comp Pay",
      count: leaveBalance.compPays ?? "-", // Dummy data for now
      icon: <FaIndianRupeeSign size={24} />,
      
      gradient: "from-blue-50 to-blue-100/50",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
      textColor: "text-blue-700",
    },
  ];

  return (
    <div className="sm:p-2 mt-4 sm:mt-auto mb-20 space-y-6 w-full sm:mx-auto min-h-screen">
      <style>{`
        .card-hover {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .card-hover:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
        }

        .announcement-item {
          transition: all 0.2s ease;
        }
        .announcement-item:hover {
          transform: translateX(5px);
        }
      `}</style>

      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-2xl font-bold text-gray-800 mb-2">
          Welcome, {currentUser?.name}
        </h1>
        <p className="text-gray-500 text-sm sm:text-base">
          Here's your dashboard overview for today
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {cards.map((card, index) => (
          <div
            key={index}
            className="overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg"
          >
            <div className="p-5 sm:p-6">
              <div className="flex items-start justify-between mb-4">
                <div
                  className={`${card.iconBg} ${card.iconColor} p-3 rounded-xl shadow-sm text-2xl`}
                >
                  {card.icon}
                </div>

      {/* Total Leaves */}
      {(card.title === "EL Balance" || card.title === "SL Balance") && (
        <div
          className={`${card.iconBg} ${card.iconColor} p-3 rounded-xl shadow-sm text-2xl font-bold`}
          >
          {card.total}
         </div>
           )}
          </div>

              <h3 className="text-sm sm:text-base font-medium text-gray-600 mb-2">
                {card.title}
              </h3>
              <p className={`text-3xl sm:text-4xl font-bold ${card.textColor}`}>
                <AnimatedCounter value={card.count} loading={loading} />
              </p>


            </div>
          </div>
        ))}
      </div>

      {/* Announcements & Holidays Section */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Announcements */}
        <div className="lg:col-span-3 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-blue-100 text-blue-600 p-2.5 rounded-xl">
                <Megaphone size={20} />
              </div>
              <h3 className="text-xl font-semibold text-gray-800">
                Announcements
              </h3>
            </div>

            {announcements.length > 0 ? (
              <div className="space-y-3">
                {announcements.map((item, i) => (
                  <div
                    key={i}
                    onClick={() =>
                      navigate(`/employee/events/detail/announcement/${item.id}`)
                    }
                    className="group p-4 bg-gradient-to-r from-blue-50 to-transparent rounded-xl hover:from-blue-100 hover:shadow-sm cursor-pointer border border-blue-100/50 announcement-item"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shriame0" />
                      <p className="text-sm text-gray-700 group-hover:text-gray-900 font-medium flex-1">
                        {item.title}
                      </p>
                    </div>
                  </div>
                ))}
                <div className="pt-4 flex justify-end">
                  <Link
                    to="/employee/events/annoucements"
                    className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors group"
                  >
                    View all announcements
                    <span className="group-hover:translate-x-1 inline-block transition-transform">
                      →
                    </span>
                  </Link>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center text-center py-12">
                <div className="bg-gray-100 rounded-full p-6 mb-4">
                  <span className="text-4xl">📭</span>
                </div>
                <p className="text-base font-medium text-gray-600 mb-1">
                  No announcements yet
                </p>
                <p className="text-sm text-gray-400">
                  Check back later for updates
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Upcoming Holidays */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-green-100 text-green-600 p-2.5 rounded-xl">
                <CalendarDays size={20} />
              </div>
              <h3 className="text-xl font-semibold text-gray-800">Holidays</h3>
            </div>

            {upcomingHolidays.length > 0 ? (
              <div className="space-y-3">
                {upcomingHolidays.map((holiday, i) => (
                  <div
                    key={i}
                    className="p-4 bg-gradient-to-r from-green-50 to-transparent rounded-xl border border-green-100/50 hover:shadow-sm transition-shadow"
                  >
                    <div className="flex flex-col gap-1">
                      <p className="font-medium text-gray-800 text-sm">
                        {holiday.holidayName}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(holiday.holidayDate).toLocaleDateString(
                          "en-IN",
                          {
                            weekday: "short",
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          }
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center text-center py-8">
                <div className="bg-gray-100 rounded-full p-5 mb-3">
                  <span className="text-3xl">🎉</span>
                </div>
                <p className="text-sm text-gray-400">No upcoming holidays</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default E_Dashboard;